import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import WheelsTimeInput from '../WheelsTimeInput/WheelsTimeInput.vue'
import WheelsAddressPicker from '../WheelsAddressPicker/WheelsAddressPicker.vue'
import WheelsDatePicker from '../WheelsDatePicker/WheelsDatePicker.vue'
import WheelsButton from '../WheelsButton/WheelsButton.vue'
import type {
  WheelsPlaceDetailStrategy,
  WheelsPickableAddress,
  WheelsSearchStrategy,
} from '../WheelsAddressPicker/WheelsAddressPicker.constants'
import {
  buildDateTime,
  dateFromDatetime,
  timeFromDatetime,
} from '@/utils/timeStrings'
import { DateTime } from 'luxon'

type ItineraryItem = {
  title?: string | null
  dropoffDatetime?: string | null
  pickupDatetime?: string | null
  travelTimeFromPreviousStopInSeconds?: number | null
  address: WheelsPickableAddress
  stopId?: number
  stopChanged?: boolean
  keepOnSite?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'WheelsItineraryEditor',
  props: {
    searchStrategy: null,
    placeDetailStrategy: null,
    itinerary: null,
    allowAddStop: { type: Boolean, default: true },
    allowRemoveStop: { type: Boolean, default: true },
    allowKeepOnSite: { type: Boolean, default: true },
    allowPickupDropoffCustomization: { type: Boolean, default: false }
  },
  emits: ["update", "address-changed"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update', itinerary: Array<ItineraryItem>): void
  (e: 'address-changed', index: number, address: WheelsPickableAddress): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { searchStrategy: WheelsSearchStrategy, placeDetailStrategy: WheelsPlaceDetailStrategy, itinerary: Array<ItineraryItem>, allowAddStop: boolean, allowRemoveStop: boolean, allowKeepOnSite: boolean, allowPickupDropoffCustomization: boolean };



const itinerary = ref(props.itinerary)



const stopErrors = computed(() =>
  itinerary.value.map((item, index) => getStopError(item, index)),
)

const hasStopError = (index: number) => Boolean(stopErrors.value[index])

watch(
  itinerary,
  (newItinerary) => {
    emit('update', newItinerary)
  },
  { deep: true },
)

onMounted(() => {
  while (itinerary.value.length < 2) {
    addEmptyStop()
  }
})

function addEmptyStop(index?: number) {
  const emptyStop = {
    address: {},
    pickupDatetime: null,
    dropoffDatetime: null,
    travelTimeFromPreviousStopInSeconds: null,
  }
  if (index === undefined) {
    itinerary.value.push(emptyStop)
  } else {
    itinerary.value.splice(index + 1, 0, emptyStop)
  }
}

function isFirstStop(index: number): boolean {
  return index === 0
}

function isLastStop(index: number): boolean {
  return index === itinerary.value.length - 1
}

function formatTravelTime(seconds: number | null): string {
  if (!seconds) return ''

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) {
    return `${minutes} min`
  } else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
}

const stopTypeLabel = (index: number) => {
  if (isFirstStop(index)) {
    return 'Pickup'
  }
  if (isLastStop(index)) {
    return 'Dropoff'
  }
  return 'Stop'
}

function handleRemoveStopClick(index: number) {
  itinerary.value.splice(index, 1)
}

function handleAddStopClick(index: number) {
  addEmptyStop(index)
}

function handleAddressUpdated(
  item: ItineraryItem,
  index: number,
  address: WheelsPickableAddress,
) {
  item.address = address
  emit('address-changed', index, address)
}


function getStopError(item: ItineraryItem, index: number): string | null {
  if (!item.address?.zoneId || index === 0) {
    return null
  }

  const currentZone = item.address.zoneId

  // Collect all datetimes from previous stops
  let latestPreviousDatetime: DateTime | null = null
  let latestStopIndex: number | null = null
  let latestDatetimeType: 'pickup' | 'dropoff' | null = null

  for (let i = 0; i < index; i++) {
    const previousStop = itinerary.value[i]

    if (previousStop.address?.zoneId) {
      // Check dropoff time
      if (previousStop.dropoffDatetime) {
        const dropoffTime = DateTime.fromISO(previousStop.dropoffDatetime, {
          zone: previousStop.address.zoneId,
        })
        if (!latestPreviousDatetime || dropoffTime > latestPreviousDatetime) {
          latestPreviousDatetime = dropoffTime
          latestStopIndex = i
          latestDatetimeType = 'dropoff'
        }
      }

      // Check pickup time
      if (previousStop.pickupDatetime) {
        const pickupTime = DateTime.fromISO(previousStop.pickupDatetime, {
          zone: previousStop.address.zoneId,
        })
        if (!latestPreviousDatetime || pickupTime > latestPreviousDatetime) {
          latestPreviousDatetime = pickupTime
          latestStopIndex = i
          latestDatetimeType = 'pickup'
        }
      }
    }
  }

  // Validate current stop's dropoff against latest previous datetime
  if (item.dropoffDatetime && latestPreviousDatetime) {
    const currentDropoff = DateTime.fromISO(item.dropoffDatetime, {
      zone: currentZone,
    })

    if (currentDropoff < latestPreviousDatetime) {
      return `Dropoff must be after the ${latestDatetimeType} time of Stop ${latestStopIndex! + 1} at ${latestPreviousDatetime.toFormat('h:mm a')} on ${latestPreviousDatetime.toFormat('LL/dd/yyyy')}`
    }
  }

  // Validate current stop's pickup against its own dropoff
  if (item.pickupDatetime && item.dropoffDatetime) {
    const currentPickup = DateTime.fromISO(item.pickupDatetime, {
      zone: currentZone,
    })
    const currentDropoff = DateTime.fromISO(item.dropoffDatetime, {
      zone: currentZone,
    })

    if (currentPickup < currentDropoff) {
      return `Pickup must be after the dropoff time of ${currentDropoff.toFormat('h:mm a')} on ${currentDropoff.toFormat('LL/dd/yyyy')}`
    }
  }

  // Validate current stop's pickup against latest previous datetime
  if (item.pickupDatetime && latestPreviousDatetime) {
    const currentPickup = DateTime.fromISO(item.pickupDatetime, {
      zone: currentZone,
    })

    if (currentPickup < latestPreviousDatetime) {
      return `Pickup must be after the ${latestDatetimeType} time of Stop ${latestStopIndex! + 1} at ${latestPreviousDatetime.toFormat('h:mm a')} on ${latestPreviousDatetime.toFormat('LL/dd/yyyy')}`
    }
  }

  return null
}

function handlePickupTimeChange(item: ItineraryItem, newTime: string | null) {
  // TODO: It might make sense to use the user's timezone here?
  if (!item.address?.zoneId) {
    console.warn('Cannot set pickup time without timezone information')
    return
  }

  // Since we don't currently support clearing times, we won't allow empty values
  if (!newTime) {
    return
  }

  // Extract existing date, or default to today if no existing datetime
  const existingDate = item.pickupDatetime
    ? dateFromDatetime(item.pickupDatetime, item.address.zoneId)
    : DateTime.now().setZone(item.address.zoneId).toFormat('yyyy-MM-dd')

  try {
    // Build new complete datetime using 24-hour format
    const newPickupDatetime = buildDateTime(
      existingDate,
      newTime,
      item.address.zoneId,
    )

    item.pickupDatetime = newPickupDatetime
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from time change:', error)
  }
}

function handlePickupDateChange(item: ItineraryItem, newDate: string) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set pickup date without timezone information')
    return
  }

  // Extract existing time, or default to 12:00 if no existing datetime
  const existingTime = item.pickupDatetime
    ? timeFromDatetime(item.pickupDatetime, item.address.zoneId)
    : '12:00 PM'

  try {
    const newPickupDatetime = buildDateTime(
      newDate,
      existingTime,
      item.address.zoneId,
    )
    item.pickupDatetime = newPickupDatetime
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from date change:', error)
  }
}

function handleDropoffTimeChange(item: ItineraryItem, newTime: string | null) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set dropoff time without timezone information')
    return
  }

  if (!newTime) return

  const existingDate = item.dropoffDatetime
    ? dateFromDatetime(item.dropoffDatetime, item.address.zoneId)
    : DateTime.now().setZone(item.address.zoneId).toFormat('yyyy-MM-dd')

  try {
    item.dropoffDatetime = buildDateTime(existingDate, newTime, item.address.zoneId)
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from time change:', error)
  }
}

function handleDropoffDateChange(item: ItineraryItem, newDate: string) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set dropoff date without timezone information')
    return
  }

  const existingTime = item.dropoffDatetime
    ? timeFromDatetime(item.dropoffDatetime, item.address.zoneId)
    : '12:00 PM'

  try {
    item.dropoffDatetime = buildDateTime(newDate, existingTime, item.address.zoneId)
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from date change:', error)
  }
}

function handleClearDropoffRow(item: ItineraryItem) {
  item.dropoffDatetime = null
  emit('update', itinerary.value)
}

function handleClearPickupRow(item: ItineraryItem) {
  item.pickupDatetime = null
  emit('update', itinerary.value)
}

return { __sfc: true,props, itinerary, emit, stopErrors, hasStopError, addEmptyStop, isFirstStop, isLastStop, formatTravelTime, stopTypeLabel, handleRemoveStopClick, handleAddStopClick, handleAddressUpdated, getStopError, handlePickupTimeChange, handlePickupDateChange, handleDropoffTimeChange, handleDropoffDateChange, handleClearDropoffRow, handleClearPickupRow, WheelsTimeInput, WheelsAddressPicker, WheelsDatePicker, WheelsButton, dateFromDatetime, timeFromDatetime }
}

})